import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Project } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Input,
  Label,
  FormGroup,
  TextArea,
  Select
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaArrowLeft, 
  FaSave, 
  FaTimes, 
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFileAlt,
  FaPlus,
  FaMinus
} from 'react-icons/fa';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    font-size: 1.8rem;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--light-gray);
  color: var(--secondary-color);
  
  &:hover {
    background: var(--medium-gray);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const FormCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  
  .section-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--secondary-color);
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  
  .form-row-full {
    margin-bottom: 1rem;
  }
`;

const ItemList = styled.div`
  margin-top: 1rem;
`;

const ItemCard = styled.div`
  background: var(--light-gray);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid var(--medium-gray);
  
  .item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    .item-title {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1rem;
    }
    
    .remove-button {
      background: var(--danger-color);
      color: white;
      border: none;
      border-radius: 4px;
      padding: 0.25rem 0.5rem;
      cursor: pointer;
      font-size: 0.8rem;
      
      &:hover {
        background: #c82333;
      }
    }
  }
  
  .item-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
`;

const AddItemButton = styled(Button)`
  background: var(--success-color);
  color: white;
  margin-top: 1rem;
  
  &:hover {
    background: #28a745;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const CancelButton = styled(Button)`
  background: var(--light-gray);
  color: var(--secondary-color);
  
  &:hover {
    background: var(--medium-gray);
  }
`;

interface RequestItem {
  id: string;
  description: string;
  category: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface FormData {
  projectId: string;
  timeline: string;
  description: string;
  priority: string;
  items: RequestItem[];
}

const SupervisorFundRequestForm: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignedProject, setAssignedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    projectId: '',
    timeline: '1 week',
    description: '',
    priority: 'Medium',
    items: []
  });
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'warning' | 'info'
  });

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const fetchAssignedProject = async () => {
      try {
        // Get projects assigned to the current supervisor
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        // Backend now properly filters by role, so we can use the first project
        const project = assignedProjects.length > 0 ? assignedProjects[0] : null;
        
        if (project) {
          setAssignedProject(project);
          setFormData(prev => ({ ...prev, projectId: project._id || project.id || '' }));
        }
      } catch (error) {
        console.error('Error fetching assigned project:', error);
      }
    };

    if (user) {
      fetchAssignedProject();
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addItem = () => {
    const newItem: RequestItem = {
      id: Date.now().toString(),
      description: '',
      category: 'Materials',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const removeItem = (itemId: string) => {
    setFormData(prev => ({ 
      ...prev, 
      items: prev.items.filter(item => item.id !== itemId) 
    }));
  };

  const updateItem = (itemId: string, field: keyof RequestItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.projectId || formData.items.length === 0 || !formData.description) {
      showDialog('Validation Error', 'Please fill in all required fields and add at least one item.', 'warning');
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare items in the format expected by backend
      const items = formData.items.map(item => ({
        description: item.description,
        category: item.category,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.total
      }));
      
      // Prepare fund request data for backend
      const requestData = {
        projectId: formData.projectId,
        items: items,
        timeline: formData.timeline,
        justification: formData.description,
        requesterName: user?.name,
        requesterId: user?.id,
        requesterRole: user?.role
      };
      
      console.log('Submitting fund request:', requestData);
      
      // Submit to backend API
      const response = await apiService.createFundRequest(requestData);
      
      if (response.success) {
        console.log('Fund request created successfully:', response.data);
        showDialog('Success', 'Fund request submitted successfully!', 'success');
        setTimeout(() => {
          navigate('/supervisor/funds');
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to create fund request');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showDialog('Error', `Error submitting fund request: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/supervisor/funds');
  };

  if (!assignedProject) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>New Fund Request</h1>
          <BackButton onClick={() => navigate('/supervisor/funds')}>
            <IconWrapper icon={FaArrowLeft} />
            Back to Requests
          </BackButton>
        </PageHeader>
        
        <FormCard>
          <CardBody>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏗️</div>
              <h3>No Project Assigned</h3>
              <p>You are not currently assigned to supervise any project. Please contact your project manager or administrator.</p>
            </div>
          </CardBody>
        </FormCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>New Fund Request</h1>
        <BackButton onClick={() => navigate('/supervisor/funds')}>
          <IconWrapper icon={FaArrowLeft} />
          Back to Requests
        </BackButton>
      </PageHeader>

      <FormCard>
        <CardHeader>
          <h2>Create New Fund Request</h2>
          <p>Project: {assignedProject.name}</p>
        </CardHeader>
        
        <CardBody>
          <form onSubmit={handleSubmit}>
            <FormSection>
              <div className="section-title">
                <IconWrapper icon={FaFileAlt} />
                Request Details
              </div>
              
              <div className="form-row">
                <FormGroup>
                  <Label>Timeline</Label>
                  <Select 
                    name="timeline" 
                    value={formData.timeline} 
                    onChange={handleInputChange}
                    required
                  >
                    <option value="1 week">1 week</option>
                    <option value="2 weeks">2 weeks</option>
                    <option value="1 month">1 month</option>
                    <option value="3 months">3 months</option>
                    <option value="6 months">6 months</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Priority</Label>
                  <Select 
                    name="priority" 
                    value={formData.priority} 
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </Select>
                </FormGroup>
              </div>
              
              <div className="form-row">
                <FormGroup>
                  <Label>Total Amount</Label>
                  <Input 
                    type="text" 
                    value={`TZS ${calculateTotal().toLocaleString()}`}
                    readOnly
                    style={{ backgroundColor: 'var(--light-gray)' }}
                  />
                </FormGroup>
              </div>
              
              <div className="form-row-full">
                <FormGroup>
                  <Label>Description</Label>
                  <TextArea 
                    name="description" 
                    value={formData.description} 
                    onChange={handleInputChange}
                    placeholder="Describe the purpose and details of this fund request..."
                    rows={4}
                    required
                  />
                </FormGroup>
              </div>
            </FormSection>

            <FormSection>
              <div className="section-title">
                <IconWrapper icon={FaMoneyBillWave} />
                Request Items
              </div>
              
              <ItemList>
                {formData.items.map((item, index) => (
                  <ItemCard key={item.id}>
                    <div className="item-header">
                      <div className="item-title">Item {index + 1}</div>
                      <button 
                        type="button"
                        className="remove-button"
                        onClick={() => removeItem(item.id)}
                      >
                        <IconWrapper icon={FaMinus} />
                        Remove
                      </button>
                    </div>
                    
                                         <div className="item-details">
                       <FormGroup>
                         <Label>Description</Label>
                         <Input 
                           type="text" 
                           value={item.description} 
                           onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                           placeholder="Item description..."
                           required
                         />
                       </FormGroup>
                       
                       <FormGroup>
                         <Label>Category</Label>
                         <Select 
                           value={item.category} 
                           onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                           required
                         >
                           <option value="Materials">Materials</option>
                           <option value="Labor">Labor</option>
                           <option value="Equipment">Equipment</option>
                           <option value="Sub-Contracts">Sub-Contracts</option>
                           <option value="Others">Others</option>
                         </Select>
                       </FormGroup>
                       
                       <FormGroup>
                         <Label>Quantity</Label>
                         <Input 
                           type="number" 
                           value={item.quantity} 
                           onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                           min="1"
                           required
                         />
                       </FormGroup>
                       
                       <FormGroup>
                         <Label>Unit Price (TZS)</Label>
                         <Input 
                           type="number" 
                           value={item.unitPrice} 
                           onChange={(e) => updateItem(item.id, 'unitPrice', parseInt(e.target.value) || 0)}
                           min="0"
                           required
                         />
                       </FormGroup>
                       
                       <FormGroup>
                         <Label>Total (TZS)</Label>
                         <Input 
                           type="text" 
                           value={`TZS ${item.total.toLocaleString()}`}
                           readOnly
                           style={{ backgroundColor: 'var(--light-gray)' }}
                         />
                       </FormGroup>
                     </div>
                  </ItemCard>
                ))}
              </ItemList>
              
              <AddItemButton type="button" onClick={addItem}>
                <IconWrapper icon={FaPlus} />
                Add Item
              </AddItemButton>
            </FormSection>

            <FormActions>
              <CancelButton type="button" onClick={handleCancel}>
                <IconWrapper icon={FaTimes} />
                Cancel
              </CancelButton>
              <Button type="submit" disabled={loading || formData.items.length === 0}>
                <IconWrapper icon={FaSave} />
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            </FormActions>
          </form>
        </CardBody>
      </FormCard>
      
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />
    </PageContainer>
  );
};

export default SupervisorFundRequestForm; 